<template>
  <div>
    <!-- Header + Filters -->
    <b-row class="align-items-center mb-1 no-print">
      <b-col>
        <div class="d-flex align-items-center justify-content-between flex-wrap">
          <div>
            <h1 class="font-weight-bolder mb-0">
              Almacén
            </h1>
          </div>
        </div>
      </b-col>

      <b-col
        cols="auto"
        class="no-print"
      >
        <div class="d-flex align-items-center flex-wrap justify-content-end">
          <!-- Folio -->
          <div
            class="d-flex align-items-center mr-2"
            style="flex:0 0 auto;"
          >
            <b-input-group size="sm">
              <b-form-input
                v-model.trim="inputFolio"
                placeholder="Buscar folio (F1234 o 1234)"
                :disabled="warehouseLoading"
                style="width: 220px;"
                @keyup.enter="applySearch"
              />
              <b-input-group-append>
                <b-button
                  variant="primary"
                  :disabled="warehouseLoading"
                  @click="applySearch"
                >
                  Buscar
                </b-button>
                <b-button
                  variant="outline-secondary"
                  :disabled="warehouseLoading || (!inputFolio && !inputRate)"
                  @click="clearSearch"
                >
                  Limpiar
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </div>

          <!-- IVA -->
          <div
            class="d-flex align-items-center mr-2"
            style="flex:0 0 auto;"
          >
            <b-input-group size="sm">
              <b-input-group-prepend is-text>
                IVA
              </b-input-group-prepend>
              <b-form-input
                v-model.number="inputRate"
                type="number"
                min="0"
                max="1"
                step="0.01"
                :disabled="warehouseLoading"
                style="width: 90px;"
                placeholder="0.16"
                @keyup.enter="applySearch"
              />
            </b-input-group>
          </div>

          <!-- Month -->
          <div
            class="d-flex align-items-center mr-2"
            style="flex:0 0 auto;"
          >
            <b-input-group size="sm">
              <b-input-group-prepend is-text>
                Mes
              </b-input-group-prepend>
              <b-form-input
                v-model="inputMonth"
                type="month"
                :max="currentMonth"
                :disabled="warehouseLoading"
                style="width: 160px;"
                @change="applySearch"
              />
            </b-input-group>
          </div>

          <b-button
            variant="outline-secondary"
            size="sm"
            class="ml-1"
            :disabled="warehouseLoading || warehouseRows.length === 0"
            @click="exportExcel"
          >
            Exportar Excel
          </b-button>

          <b-button
            variant="outline-secondary"
            size="sm"
            class="ml-1"
            @click="printPage"
          >
            Imprimir
          </b-button>
        </div>
      </b-col>
    </b-row>

    <!-- Totals Cards -->
    <b-row
      v-if="showTotals"
      class="mb-1 no-print"
    >
      <b-col cols="12">
        <b-card
          class="totals-surface"
          body-class="p-2"
        >
          <div class="d-flex align-items-start justify-content-between flex-wrap">
            <div class="mb-1 mb-md-0">
              <div class="d-flex align-items-center">
                <div class="totals-title mr-1">
                  Totales del periodo
                </div>
                <b-badge
                  class="totals-pill"
                >
                  {{ monthLabel }}
                </b-badge>
              </div>
              <div class="text-muted small">
                Incluye flete (considerado como insumo). Comisión % = comisión total / ventas totales.
              </div>
            </div>

            <div class="text-right">
              <div class="text-muted small">
                Cotizaciones
              </div>
              <div class="font-weight-bolder">
                {{ uniqueQuoteCount }}
              </div>
            </div>
          </div>

          <b-row class="mt-1">
            <!-- Ventas (principal) -->
            <b-col
              cols="12"
              md="4"
              class="mb-1 mb-md-0"
            >
              <div class="metric-card metric-primary">
                <div class="metric-label">
                  Total ventas
                </div>
                <div class="metric-value">
                  {{ money(totals.sale_total) }}
                </div>
                <div class="metric-sub">
                  Base del % de comisión
                </div>
              </div>
            </b-col>

            <!-- Compras -->
            <b-col
              cols="12"
              md="4"
              class="mb-1 mb-md-0"
            >
              <div class="metric-card">
                <div class="metric-label">
                  Total compras
                </div>
                <div class="metric-value">
                  {{ money(totals.purchase_total) }}
                </div>
                <div class="metric-sub">
                  Importe total a proveedor
                </div>
              </div>
            </b-col>

            <!-- Comisión -->
            <b-col
              cols="12"
              md="4"
            >
              <div class="metric-card">
                <div class="d-flex align-items-baseline justify-content-between">
                  <div>
                    <div class="metric-label">
                      Comisión total
                    </div>
                    <div class="metric-value">
                      {{ money(totals.commission_total) }}
                    </div>
                  </div>

                  <div class="text-right">
                    <div class="metric-label">
                      % comisión
                    </div>
                    <div class="metric-value metric-compact">
                      {{ pct(totals.commission_pct) }}
                    </div>
                  </div>
                </div>

                <div class="metric-sub">
                  Margen bruto del periodo
                </div>
              </div>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>

    <!-- Table -->
    <b-card class="p-0">
      <WarehouseTable
        ref="warehouseTable"
        :rows="warehouseRows"
        :loading="warehouseLoading"
        :money="money"
        :format-date="formatDate"
        @sort-changed="onSortChanged"
      />
      <b-alert
        v-if="!warehouseLoading && warehouseRows.length === 0"
        show
        variant="light"
        class="mb-0"
      >
        Sin resultados.
      </b-alert>
    </b-card>
  </div>
</template>

<script>
import { saveAs } from 'file-saver'
import { mapGetters, mapActions } from 'vuex'
import WarehouseTable from '@/components/warehouse/WarehouseTable.vue'

export default {
  name: 'Warehouse',
  components: { WarehouseTable },

  data() {
    return {
      inputFolio: this.$route.query.folio || '',
      inputRate: this.$route.query.default_tax_rate
        ? Number(this.$route.query.default_tax_rate)
        : null,
      inputMonth: this.$route.query.month || '',
    }
  },

  computed: {
    ...mapGetters('warehouse', [
      'warehouseRows',
      'warehouseLoading',
      'warehouseFilters',
      'warehousePagination',
      'warehouseTotals',
    ]),

    // ✅ soporta totals plano o legacy totals.with_freight
    totals() {
      return this.warehouseTotals?.with_freight || this.warehouseTotals || {}
    },

    showTotals() {
      return !!this.totals && Object.keys(this.totals).length > 0
    },

    currentMonth() {
      const d = new Date()
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const yyyy = d.getFullYear()
      return `${yyyy}-${mm}`
    },

    monthLabel() {
      const m = this.inputMonth || this.$route.query.month || this.currentMonth
      if (!m) return '—'
      const [yyyy, mm] = m.split('-')
      const monthNames = [
        'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
        'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic',
      ]
      const idx = Number(mm) - 1
      const mon = monthNames[idx] || mm
      return `${mon} ${yyyy}`
    },

    uniqueQuoteCount() {
      const keys = new Set()

      const rows = this.warehouseRows || []
      rows.forEach(row => {
        const key = row.group_key || row.quote_id || row.folio
        if (key) keys.add(String(key))
      })

      return keys.size
    },
  },

  watch: {
    '$route.query': function routeQueryWatcher(q) {
      this.inputFolio = q.folio || ''
      this.inputRate = q.default_tax_rate ? Number(q.default_tax_rate) : null
      this.inputMonth = q.month || this.inputMonth || this.currentMonth

      this.setWarehouseFilter({ field: 'folio', value: this.inputFolio })
      this.setWarehouseFilter({ field: 'default_tax_rate', value: this.inputRate })
      this.setWarehouseFilter({ field: 'month', value: this.inputMonth })

      this.fetchWarehouse()
    },
  },

  created() {
    // Asegurar mes activo
    let monthFromRoute = this.$route.query.month
    if (!monthFromRoute) {
      monthFromRoute = this.currentMonth
      this.$router.replace({ query: { ...this.$route.query, month: monthFromRoute } }).catch(() => {})
    }
    this.inputMonth = monthFromRoute

    if (this.$route.query.folio) {
      this.setWarehouseFilter({ field: 'folio', value: this.$route.query.folio })
    }
    if (this.$route.query.default_tax_rate !== undefined) {
      this.setWarehouseFilter({
        field: 'default_tax_rate',
        value: Number(this.$route.query.default_tax_rate),
      })
    }

    this.setWarehouseFilter({ field: 'month', value: this.inputMonth })
    this.fetchWarehouse()
  },

  methods: {
    ...mapActions('warehouse', [
      'fetchWarehouse',
      'setWarehouseFilter',
      'applyWarehouseFilters',
      'clearWarehouseFilters',
      'changeWarehousePagination',
    ]),

    applySearch() {
      this.setWarehouseFilter({ field: 'folio', value: this.inputFolio })
      this.setWarehouseFilter({ field: 'default_tax_rate', value: this.inputRate })
      this.setWarehouseFilter({ field: 'month', value: this.inputMonth || this.currentMonth })

      const query = {}

      if (this.inputFolio) query.folio = this.inputFolio
      if (this.inputRate !== null && this.inputRate !== undefined && this.inputRate !== '') {
        query.default_tax_rate = this.inputRate
      }
      query.month = this.inputMonth || this.currentMonth

      this.$router.replace({ query }).catch(() => {})
      this.applyWarehouseFilters()
    },

    clearSearch() {
      this.inputFolio = ''
      this.inputRate = null

      const query = { month: this.inputMonth || this.currentMonth }
      this.$router.replace({ query }).catch(() => {})

      this.clearWarehouseFilters()
      this.setWarehouseFilter({ field: 'month', value: this.inputMonth || this.currentMonth })
      this.fetchWarehouse()
    },

    onSortChanged() {},

    printPage() {
      window.print()
    },

    exportExcel() {
      const tableHtml = this.$refs.warehouseTable?.getExcelTableHtml?.()
      if (!tableHtml) return

      const activeMonth = this.inputMonth || this.currentMonth
      const filters = [
        { label: 'Periodo', value: this.monthLabel },
        { label: 'Folio', value: this.inputFolio || 'Todos' },
        { label: 'IVA', value: this.inputRate !== null && this.inputRate !== undefined && this.inputRate !== '' ? this.pct(this.inputRate) : 'Predeterminado' },
        { label: 'Cotizaciones', value: String(this.uniqueQuoteCount) },
      ]

      const filtersHtml = `
        <table class="export-filters">
          ${filters.map(filter => `
            <tr>
              <td class="filter-label">${filter.label}</td>
              <td>${filter.value}</td>
            </tr>
          `).join('')}
        </table>
      `

      const totalsHtml = this.showTotals
        ? `
          <table class="export-totals">
            <tr>
              <td class="export-metric export-primary">
                <div class="metric-label">Total ventas</div>
                <div class="metric-value">${this.money(this.totals.sale_total)}</div>
                <div class="metric-sub">Base del % de comisión</div>
              </td>
              <td class="export-metric">
                <div class="metric-label">Total compras</div>
                <div class="metric-value">${this.money(this.totals.purchase_total)}</div>
                <div class="metric-sub">Importe total a proveedor</div>
              </td>
              <td class="export-metric">
                <div class="metric-label">Comisión total</div>
                <div class="metric-value">${this.money(this.totals.commission_total)}</div>
                <div class="metric-sub">Comisión %: ${this.pct(this.totals.commission_pct)}</div>
              </td>
            </tr>
          </table>
        `
        : ''

      const html = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office"
              xmlns:x="urn:schemas-microsoft-com:office:excel"
              xmlns="http://www.w3.org/TR/REC-html40">
          <head>
            <meta charset="UTF-8">
            <!--[if gte mso 9]>
              <xml>
                <x:ExcelWorkbook>
                  <x:ExcelWorksheets>
                    <x:ExcelWorksheet>
                      <x:Name>Almacén</x:Name>
                      <x:WorksheetOptions>
                        <x:DisplayGridlines/>
                      </x:WorksheetOptions>
                    </x:ExcelWorksheet>
                  </x:ExcelWorksheets>
                </x:ExcelWorkbook>
              </xml>
            <![endif]-->
            <style>
              body { font-family: Arial, Helvetica, sans-serif; color: #3b3f5c; margin: 24px; }
              h1 { margin: 0 0 4px 0; font-size: 24px; }
              .export-subtitle { margin: 0 0 16px 0; color: #6e6b7b; font-size: 13px; }
              table { border-collapse: collapse; }
              .export-filters { margin-bottom: 16px; min-width: 360px; }
              .export-filters td { border: 1px solid #dfe3e8; padding: 6px 10px; font-size: 12px; }
              .filter-label { font-weight: 700; background: #f8f9fa; width: 130px; }
              .export-totals { width: 100%; margin-bottom: 18px; }
              .export-metric { border: 1px solid #dfe3e8; padding: 10px 12px; vertical-align: top; width: 33.33%; }
              .export-primary { background: #fcfcfd; }
              .metric-label { color: #6e6b7b; font-size: 11px; font-weight: 700; text-transform: uppercase; }
              .metric-value { margin-top: 4px; font-size: 20px; font-weight: 700; }
              .metric-sub { margin-top: 4px; color: #6e6b7b; font-size: 11px; }
            </style>
          </head>
          <body>
            <h1>Almacén</h1>
            <p class="export-subtitle">Exportación con los filtros y el formato visual del reporte actual.</p>
            ${filtersHtml}
            ${totalsHtml}
            ${tableHtml}
          </body>
        </html>
      `

      const safeMonth = (activeMonth || 'sin-periodo').replace(/[^0-9-]/g, '')
      const safeFolio = (this.inputFolio || 'todos').replace(/[^a-zA-Z0-9_-]/g, '_')
      const filename = `almacen_${safeMonth}_${safeFolio}.xls`
      const blob = new Blob(['\ufeff', html], {
        type: 'application/vnd.ms-excel;charset=utf-8',
      })

      saveAs(blob, filename)
    },

    money(n) {
      if (n === null || n === undefined || n === '') return '$ 0.00'
      const num = Number(n)
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2,
      }).format(Number.isNaN(num) ? 0 : num)
    },

    pct(n) {
      const num = Number(n)
      if (Number.isNaN(num)) return '0.00%'
      return `${(num * 100).toFixed(2)}%`
    },

    formatDate(iso) {
      if (!iso) return ''

      if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
        const [yyyy, mm, dd] = iso.split('-').map(Number)
        const d = new Date(yyyy, mm - 1, dd)
        const day = String(d.getDate()).padStart(2, '0')
        const month = String(d.getMonth() + 1).padStart(2, '0')
        const year = d.getFullYear()
        return `${day}-${month}-${year}`
      }

      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return ''
      const dd = String(d.getDate()).padStart(2, '0')
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const yyyy = d.getFullYear()
      return `${dd}-${mm}-${yyyy}`
    },
  },
}
</script>

<style scoped>
/* Subtle surface for totals */
.totals-surface {
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.04);
  border-radius: 14px;
}

/* Header bits */
.totals-title {
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: 0.2px;
}
.totals-pill {
  background: rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.10);
  color: rgba(0, 0, 0, 0.78);
  font-weight: 800;
  padding: 0.22rem 0.55rem;
  border-radius: 999px;
  font-size: 0.78rem;
  letter-spacing: 0.2px;
}

/* Metric cards */
.metric-card {
  height: 100%;
  padding: 12px;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: #fff;
}
.metric-primary {
  border-color: rgba(0, 0, 0, 0.10);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05);
}
.metric-label {
  color: rgba(0, 0, 0, 0.55);
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}
.metric-value {
  margin-top: 4px;
  font-weight: 900;
  font-size: 1.35rem;
  line-height: 1.1;
}
.metric-compact {
  font-size: 1.1rem;
}
.metric-sub {
  margin-top: 6px;
  color: rgba(0, 0, 0, 0.48);
  font-size: 0.78rem;
}

/* Print */
@media print {
  .no-print { display: none !important; }
  .card, .card-body { border: none !important; box-shadow: none !important; }
}
</style>
